import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, DollarSign, Palette, Users } from 'lucide-react';

const AboutSection = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const features = [
    { 
      icon: DollarSign, 
      title: t('about.features.prices.title'), 
      desc: t('about.features.prices.description') 
    },
    { 
      icon: Palette, 
      title: t('about.features.design.title'), 
      desc: t('about.features.design.description') 
    },
    { 
      icon: Users, 
      title: t('about.features.team.title'), 
      desc: t('about.features.team.description') 
    },
  ];

  return (
    <section data-testid="about-section" className="section-padding bg-secondary grain">
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Images Grid */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 40 : -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative grid grid-cols-2 gap-4"
          >
            <div className="space-y-4">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/29925933/pexels-photo-29925933.jpeg" 
                  alt="Kitchen design"
                  loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1527501034545-bbadafbf308b" 
                  alt="Craftsmanship"
                  loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="pt-12">
              <div className="aspect-[3/4] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1650615653338-0ec058c99fa1" 
                  alt="Wood detail"
                  loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Floating Badge */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, type: 'spring' }}
              className="absolute -bottom-6 end-0 bg-gold text-charcoal p-6 shadow-xl"
            >
              <span className="text-4xl font-bold">10+</span>
              <span className="block text-sm uppercase tracking-wider mt-1">
                {isRTL ? 'سنوات خبرة' : 'Years Experience'}
              </span>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -40 : 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="label-text mb-4 block">{t('about.label')}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
              {t('about.title')}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              {t('about.description')}
            </p>

            {/* Features */}
            <div className="space-y-6 mb-10">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="text-gold" size={22} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{feature.title}</h4>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Link
              to="/about"
              data-testid="about-read-more"
              className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-gold hover:text-charcoal transition-colors duration-300"
            >
              {t('about.readMore')}
              <Arrow size={18} className="group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
